<script setup lang="ts">
import { useGoogleAutocomplete } from "@/utils/useGoogleAutocomplete";
import { ref, defineEmits, defineProps, withDefaults, watch } from "vue";
import { PlacePrediction } from "@/types/calculators";
import { PlaceSelectedPayload } from "@/types/google-places";

const props = withDefaults(
  defineProps<{
    searchType?: "city" | "address";
    styleType?: "filled" | "outline";
    initialQuery?: string;
  }>(),
  {
    searchType: "city",
    styleType: "filled",
  }
);

const emits = defineEmits<{
  (e: "placeSelected", payload: PlaceSelectedPayload): void;
}>();

watch(
  () => props.initialQuery,
  (newQuery: string | undefined) => {
    placeQuery.value = newQuery ?? "";
  }
);

let timeout: number | null = null;
const googleMapKey = process.env.VUE_APP_GOOGLE_MAP_KEY;
const placeQuery = ref("");

const { options, fetchSuggestions, getGeocoder } = useGoogleAutocomplete(
  "ru",
  props.searchType
);

function onInput() {
  if (timeout) clearTimeout(timeout);
  timeout = window.setTimeout(() => fetchSuggestions(placeQuery.value), 700);
}

async function selectPlace(city: PlacePrediction) {
  placeQuery.value = city.label;
  try {
    const geocoder = await getGeocoder();
    if (geocoder) {
      await getGeocoderLocation(geocoder, city);
    } else {
      await getApproximatelyLocation(city);
    }
  } catch (e) {
    console.error("Error getting prediction place: ", e);
  }
}

async function getGeocoderLocation(
  geocoder: google.maps.Geocoder,
  city: PlacePrediction
) {
  const result = await geocoder.geocode({ address: city.label });
  if (!result || result.results.length < 0) {
    throw new Error("Geocode api didn't send a response");
  }

  const location = result.results[0].geometry.location;
  const timeZone = await fetchTimeZome(location);
  emits("placeSelected", {
    location: location,
    timeZone: timeZone,
    locationName: city.city ?? city.label,
  });
}

async function getApproximatelyLocation(city: PlacePrediction) {
  const place = city.place;
  await place.fetchFields({ fields: ["location"] });

  const location = city.place.location;
  if (location) {
    const timeZone = await fetchTimeZome(location);
    emits("placeSelected", {
      location: location,
      timeZone: timeZone,
      locationName: city.label,
    });
  }
}

async function fetchTimeZome(location: google.maps.LatLng) {
  const lat = location.lat();
  const lng = location.lng();
  const response = await fetch(
    `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${Math.floor(
      Date.now() / 1000
    )}&key=${googleMapKey}`
  );
  const data = await response.json();
  return data.timeZoneId;
}
</script>

<template>
  <div class="relative w-full group">
    <input
      id="place"
      type="text"
      v-model="placeQuery"
      @input="onInput"
      autocomplete="off"
      class="border pl-4 pr-[6px] w-full h-[52px] rounded-[14px] sf-pro font-normal text-base focus:outline-none"
      :class="{
        'border-divider bg-secondary text-muted': styleType === 'filled',
        'border-muted text-black': styleType === 'outline',
      }"
    />

    <ul
      v-if="options.length"
      class="absolute left-0 right-0 bg-white border rounded mt-1 max-h-60 overflow-y-auto z-10 hidden group-focus-within:block"
    >
      <li
        v-for="option in options"
        :key="option.label"
        @mousedown="selectPlace(option)"
        class="px-3 py-2 hover:bg-gray-100 cursor-pointer text-left"
      >
        {{ option.label }}
      </li>
    </ul>
  </div>
</template>
